<template>
  <div
    v-if="dsComponentDetails.version === VersionOptions.LEGACY"
    ref="tabList"
    class="sb-tabs"
  >
    <template
      v-for="(tab, i) in tabs"
      :key="tab.id"
    >
      <template v-if="visibleQty == null || i <= visibleQty - 1">
        <template v-if="$slots['tab-template']">
          <slot
            name="tab-template"
            :tab="tab"
          />
        </template>
        <SbTab
          v-else
          :tab="tab.id"
        >
          <span>
            <sb-text
              :data-sb-automation="tab.name"
              :value="tab.name"
            />
          </span>
        </SbTab>
      </template>
    </template>
    <div v-click-outside="hideDropdownOptions">
      <SbTab
        v-if="visibleQty < tabs.length"
        tab="more"
        :delimit="true"
      >
        <span
          ref="activator"
          :class="{
            'sb-tab--active': activeInDropdown,
          }"
        ><sb-text value="common.more" /></span>
        <i
          aria-hidden="true"
          class="sb-icon-caret-down"
          :class="{
            'sb-tabs__sb-icon-caret-down': true,
            'sb-tabs__sb-icon-caret-down--active': showMore
          }"
        />
        <SbPopover
          :target="popoverTarget"
          :active="showMore"
          placement="start"
          orientation="bottom"
        >
          <FadeTransition>
            <div
              v-show="showMore"
              class="sb-tabs__more-dropdown"
            >
              <template
                v-for="tab in remainingTabs"
                :key="tab.id"
              >
                <template v-if="$slots['tab-more-template']">
                  <slot
                    name="tab-more-template"
                    :tab="tab"
                  />
                </template>
                <SbTab
                  v-else
                  :tab="tab.id"
                >
                  <span><sb-text :value="tab.name" /></span>
                </SbTab>
              </template>
            </div>
          </FadeTransition>
        </SbPopover>
      </SbTab>
    </div>
  </div>
  <SbTabsListNext
    v-else
    :tabs="tabs"
    v-bind="$attrs"
    :value="value"
    @update:value="$emit('update:value', $event)"
  >
    <template
      v-if="$slots['tab-template']"
      #tab-template="{tab}"
    >
      <slot
        name="tab-template"
        :tab="tab"
        version="next"
      />
    </template>
  </SbTabsListNext>
</template>
<script>
import debounce from 'lodash/debounce';
import SbPopover from '@src/components/Popover/SbPopover.vue';
import FadeTransition from '@src/components/Transitions/Fade.vue';
import ClickOutsideDirectiveMixin from '@src/utils/directives/clickOutsideDirective';
import SbText from '@src/components/Text/SbText.vue';
import { versionedComponentsMixin } from '@src/mixins/versionedComponents';
import SbTab from '@src/components/Tabs/components/SbTab.vue';
import { getElementWidths, getVisibileQuantity } from '@src/components/Tabs/utils/tabsListVisibility';
import SbTabsListNext from '@src/components/Tabs/SbTabsListNext.vue';

export default {
  name: 'SbTabsList',
  components: {
    SbTabsListNext,
    SbTab,
    SbPopover,
    FadeTransition,
    SbText,
  },
  mixins: [ClickOutsideDirectiveMixin, versionedComponentsMixin],
  provide() {
    return {
      sbTabs: this.sbTabs,
      setActiveTab: this.setActiveTab,
    };
  },
  props: {
    /**
     * List of tabs objects to be displayed.
     * Each tab requires an `id` and `name` attribute
     */
    tabs: {
      type: Array,
      default: () => [],
    },
    /**
     * Used to bind the active SbTab. Allows you to use the `v-model` syntax.
     */
    value: {
      default: null,
      type: [String, Number],
    },
  },
  emits: [
    /**
     * Emitted with the new tab value
     */
    'update:value',
  ],
  data: () => ({
    sbTabs: {
      activeTab: null,
    },
    visibleQty: null,
    tabWidths: [],
    totalWidth: 0,
    moreButtonWidth: 0,
    showMore: false,
  }),
  computed: {
    popoverTarget() {
      return this.$refs.activator;
    },
    /**
     * Indicates if the active tab is in the 'More' dropdown
     */
    activeInDropdown() {
      const index = this.tabs.findIndex((t) => t.id === this.sbTabs.activeTab);
      return (index >= this.visibleQty);
    },

    remainingTabs() {
      return this.tabs.slice(this.visibleQty);
    },

  },
  watch: {
    value: {
      // update local activeTab on parent change
      handler(newVal) {
        this.sbTabs.activeTab = newVal;
      },
      immediate: true,
    },
    tabs: {
      // if new tabs dynamically added, recalculate widths
      handler() {
        if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
        this.visibleQty = null;
        this.$nextTick(() => {
          this.retrieveWidths();
          this.checkVisibility();
        });
      },
      deep: true,
    },
  },
  mounted() {
    if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
    window.addEventListener('resize', this.handleResize);
    this.retrieveWidths();
    this.checkVisibility();
  },
  beforeUnmount() {
    if (this.dsComponentDetails.version === this.VersionOptions.NEXT) return;
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize: debounce(function resize() {
      this.checkVisibility();
    }, 500),
    retrieveWidths() {
      this.tabWidths.splice(0);
      const tablist = this.$refs.tabList;

      const { widths, totalElementWidth, moreButtonWidth } = getElementWidths(tablist);
      this.totalWidth = totalElementWidth;
      this.tabWidths.push(...widths);
      this.moreButtonWidth = moreButtonWidth;
    },
    checkVisibility() {
      const visibleQty = getVisibileQuantity({
        tabContainer: this.$refs.tabList,
        widths: this.tabWidths,
        totalElementWidth: this.totalWidth,
        moreButtonWidth: this.moreButtonWidth,
      });

      this.visibleQty = visibleQty;
    },
    toggleShowDropdown() {
      this.showMore = !this.showMore;
    },
    hideDropdownOptions() {
      this.showMore = false;
    },
    setActiveTab(tab) {
      if (tab === 'more') {
        this.toggleShowDropdown();
        return;
      }
      this.hideDropdownOptions();
      this.$emit('update:value', tab);
    },
  },
};
</script>
<style lang="scss">
  @import '@src/components/Tabs/style/sb-tabs.scss';

  .sb-tabs {
    box-sizing: content-box;
  }
</style>
